/**
 * errorSanitizer - Single source of truth for normalizing backend error shapes.
 *
 * RULE #16: Centralized pattern — never handle raw backend strings in UI components.
 * RULE #-2: Fix architecturally at the layer that owns error normalization (poll
 *           adapters + httpUtils), not per-component.
 *
 * CONTRACT (backend): unhandled 5xx surfaces as:
 *   { ok: false, error_code: "SERVICE_UNAVAILABLE", error_id: "<hex>" }
 * Known 4xx codes (UNAUTHORIZED, INSUFFICIENT_TOKENS, etc.) keep their specific
 * error_code; only SERVICE_UNAVAILABLE and unknown codes get the generic message.
 *
 * The generic message makes NO refund claim. Most failures that reach it charged
 * nothing (a failed status GET, a network error, a cancel request) or are not
 * refunded on failure (a YouTube publish keeps its settled LLM calls charged).
 * Only a payload that itself carries `tokens_refunded: true` -- i.e. the party
 * that performed the refund says so -- gets the "tokens refunded" variant.
 *
 * NEVER expose raw backend strings to the user — this module is the firewall.
 */

import * as Sentry from '@sentry/react';
import { API_ERROR_CODES } from '../constants/apiErrorCodes';

export interface SanitizedError {
  /** i18n key for the user-facing message */
  i18nKey: string;
  /** Backend-provided error_id for support correlation (optional) */
  errorId?: string;
  /** Original error_code — for analytics/debugging ONLY, never displayed raw */
  errorCode?: string;
}

// ---------------------------------------------------------------------------
// Known 4xx error codes that have user-actionable existing i18n messages.
// Map them to the key that already exists in en.json so those messages are
// preserved. Any code NOT in this map → generic service_unavailable.
// ---------------------------------------------------------------------------
const KNOWN_4XX_MAP: Record<string, string> = {
  [API_ERROR_CODES.UNAUTHORIZED]: 'error.network.authentication_failed',
  [API_ERROR_CODES.INVALID_TOKEN]: 'error.network.authentication_failed',
  [API_ERROR_CODES.TOKEN_EXPIRED]: 'error.network.authentication_failed',
  [API_ERROR_CODES.RATE_LIMITED]: 'error.network.rate_limited',
  // Was 'error.service.unavailable' — "The service is temporarily unavailable,
  // please try again later." A balance that is too low is neither temporary nor
  // the service's fault, and retrying can never fix it. The user is told what
  // actually happened, and the buy-tokens modal (useTokenGate) carries the
  // remedy on the surfaces that can open it.
  [API_ERROR_CODES.INSUFFICIENT_TOKENS]: 'tokens.insufficientTokens',
};

const GENERIC_KEY = 'error.generic.service_unavailable';
const GENERIC_REFUNDED_KEY = 'error.generic.service_unavailable_refunded';

/**
 * Normalize ANY backend error payload into a SanitizedError.
 *
 * Accepts the raw progress.json data from poll adapters, raw fetch error
 * bodies, or any unknown thrown value — returns a safe, i18n-keyed result.
 *
 * Side effect: captures the payload to Sentry (if configured) so support
 * can correlate the user-visible Reference ID to backend logs.
 */
export function sanitizeBackendError(payload: unknown): SanitizedError {
  // Extract structured fields if payload is object-shaped
  let errorCode: string | undefined;
  let errorId: string | undefined;
  let tokensRefunded = false;

  if (payload !== null && typeof payload === 'object') {
    const p = payload as Record<string, unknown>;
    if (typeof p.error_code === 'string') errorCode = p.error_code;
    if (typeof p.error_id === 'string') errorId = p.error_id;
    tokensRefunded = p.tokens_refunded === true;
    // Also handle flat { code } shape from JobError
    if (!errorCode && typeof p.code === 'string') errorCode = p.code;
  }

  // Capture to Sentry for backend log correlation
  Sentry.captureMessage('[ErrorSanitizer] Backend error surfaced to client', {
    level: 'error',
    extra: {
      rawPayload: payload,
      error_code: errorCode,
      error_id: errorId,
      tokens_refunded: tokensRefunded,
    },
  });

  // Determine i18n key
  let i18nKey = tokensRefunded ? GENERIC_REFUNDED_KEY : GENERIC_KEY;
  if (errorCode && errorCode in KNOWN_4XX_MAP) {
    i18nKey = KNOWN_4XX_MAP[errorCode] ?? i18nKey;
  }

  return { i18nKey, errorId, errorCode };
}
